/**
 * Resolves select options for registration form fields.
 * For event-sourced fields (division, flight), options come from the event; otherwise from field.options.
 */

import { applyEarlyRegDiscount, formatDisplayPrice, PriceDisplayMode, scalePriceForDisplay } from "./eventPricingUtils";

interface RegistrationOption {
  label: string;
  value: string;
}

interface FieldWithEventSource {
  name?: string;
  options?: string[];
  eventSource?: "division" | "flight";
  eventLabelField?: string;
}

interface EventWithFlighting {
  flightPricing?: boolean;
  earlyRegDiscount?: number;
  earlyRegCutoffDate?: Date | string | null;
  flighting?: {
    divisions?: Array<Record<string, unknown>>;
    flights?: Array<Record<string, unknown>>;
  };
}

/** Optional context for event-sourced fields (e.g. selected division to filter flights). */
export interface RegistrationFieldOptionsContext {
  selectedDivision?: string | null;
  priceDisplayMode?: PriceDisplayMode;
  regQty?: number;
}

export function getRegistrationFieldOptions(
  field: FieldWithEventSource,
  event: EventWithFlighting | null | undefined,
  context?: RegistrationFieldOptionsContext
): RegistrationOption[] {
  if (field.eventSource && event?.flighting) {
    const labelField =
      field.eventLabelField ??
      (field.eventSource === "division" ? "divisionName" : "flightName");
    let raw: Array<Record<string, unknown>>;
    if (field.eventSource === "division") {
      raw = event.flighting.divisions ?? [];
    } else {
      raw = event.flighting.flights ?? [];
      if (context?.selectedDivision != null && context.selectedDivision !== "") {
        const divisions = event.flighting.divisions ?? [];
        const divisionIndex = divisions.findIndex(
          (d) => String(d.divisionName ?? "") === String(context!.selectedDivision)
        );
        if (divisionIndex >= 0) {
          raw = raw.filter((f) => (f.divisionIndex as number) === divisionIndex);
        }
      }
    }
    const showPrice = event.flightPricing === true;
    const priceField =
      field.eventSource === "division" ? "divisionPrice" : "flightPrice";

    return raw
      .map((item) => {
        const label = item[labelField];
        const str =
          typeof label === "string" ? label : label != null ? String(label) : "";
        if (!str) return null;
        const price =
          showPrice && typeof item[priceField] === "number"
            ? (item[priceField] as number)
            : null;
        const displayPrice =
          price != null ? applyEarlyRegDiscount(price, event) : null;
        const scaledDisplayPrice =
          displayPrice != null
            ? scalePriceForDisplay(
                displayPrice,
                context?.regQty ?? 1,
                context?.priceDisplayMode ?? "total"
              )
            : null;
        const displayLabel =
          scaledDisplayPrice != null
            ? `${str} (${formatDisplayPrice(scaledDisplayPrice)})`
            : str;
        return { label: displayLabel, value: str };
      })
      .filter((o): o is RegistrationOption => o != null);
  }
  const opts = field.options ?? [];
  return opts.map((opt) => ({ label: opt, value: opt }));
}
